import React, { useState, useMemo } from 'react';
import { Container, Card, CardHeader, CardBody, Collapse, Input, InputGroup, InputGroupText } from 'reactstrap';
import { SEO } from 'components/SEO';

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: string | React.ReactNode;
  icon: string;
}

const FAQ_DATA: FAQItem[] = [
  {
    id: '1',
    category: 'General',
    icon: 'fas fa-info-circle',
    question: 'What is Speak on Steem (Speem)?',
    answer: <><strong>Speak on Steem (Speem)</strong> is a decentralized video platform built on the Steem blockchain. It is designed to give creators true control and ownership over their content, ensuring visibility natively without relying on closed algorithms or hidden systems.</>
  },
  {
    id: '2',
    category: 'Getting Started',
    icon: 'fas fa-cloud-upload-alt',
    question: 'How can I upload videos?',
    answer: 'You can upload videos directly from the platform using the "Upload" button after connecting your account securely via SteemKeychain or your private keys. Your videos are processed and distributed across decentralized networks like IPFS, ensuring uninterrupted global distribution.'
  },
  {
    id: '3',
    category: 'Account',
    icon: 'fas fa-user-lock',
    question: 'Do I need a Steem account to use Speem?',
    answer: "You don't need an account to watch videos and explore content. However, to interact (comment or vote), publish your own videos, and start earning crypto rewards, you must authenticate with an active Steem blockchain account."
  },
  {
    id: '4',
    category: 'Rewards',
    icon: 'fas fa-coins',
    question: 'How do I earn rewards?',
    answer: "Whenever you publish a new clip or interact by upvoting great content, you generate native cryptocurrency rewards based on the community's combined voting power (SP). Rewards are typically liquidated after a 7-day period."
  },
  {
    id: '5',
    category: 'Security',
    icon: 'fas fa-shield-alt',
    question: 'Is Speem decentralized and censorship-resistant?',
    answer: <><strong>Absolutely.</strong> All metadata, comments, and upvotes live immutably on Steem's decentralized ledgers. Coupled with our IPFS-backed storage layer, Speem ensures a model that empowers the community over central authorities.</>
  },
  {
    id: '6',
    category: 'Account',
    icon: 'fas fa-key',
    question: 'What is Steem Keychain?',
    answer: 'Steem Keychain is a browser extension that allows you to manage your Steem account and sign transactions securely without sharing your private keys with third-party sites. We highly recommend using it for a safer experience.'
  },
  {
    id: '7',
    category: 'Storage',
    icon: 'fas fa-hdd',
    question: 'Does Speem store my videos forever?',
    answer: 'Speem utilizes IPFS for video storage. While IPFS is decentralized, content is maintained as long as it is "pinned" by a node. Our platform ensures that active content remains available, but persistence also depends on the decentralized network nodes.'
  }
];

export const FAQPage = () => {
  const [open, setOpen] = useState<string | null>('1');
  const [search, setSearch] = useState('');

  const toggle = (id: string) => {
    setOpen(open === id ? null : id);
  };

  const filteredFaqs = useMemo(() => {
    if (!search) return FAQ_DATA;
    const q = search.toLowerCase();
    return FAQ_DATA.filter(item => 
      item.question.toLowerCase().includes(q) || 
      item.category.toLowerCase().includes(q) ||
      (typeof item.answer === 'string' && item.answer.toLowerCase().includes(q))
    );
  }, [search]);

  return (
    <Container className="mt-5 pb-5">
      <SEO 
        title="FAQ | Speak on Steem" 
        description="Find answers to common questions about Speem, content creation, and rewards on the Steem blockchain." 
      />

      <div className="text-center mb-5 animate__animated animate__fadeIn">
        <h1 className="font-weight-bold display-4 mb-3">
          How can we <span className="text-speem">help?</span>
        </h1>
        <p className="text-muted lead mx-auto" style={{ maxWidth: '600px' }}>
          Explore our guide to understanding decentralized video, rewards, and how to make the most of Speem.
        </p>
      </div>

      <div className="faq-search-container mx-auto mb-5" style={{ maxWidth: '700px' }}>
        <InputGroup className="fancy-input shadow-sm">
          <InputGroupText className="bg-transparent border-0 pr-0 pl-4">
            <i className="fas fa-search text-muted"></i>
          </InputGroupText>
          <Input
            className="border-0 py-4 shadow-none bg-transparent"
            placeholder="Search questions, categories, keywords..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
      </div>

      <div className="faq-list mx-auto" style={{ maxWidth: '800px' }}>
        {filteredFaqs.length > 0 ? (
          filteredFaqs.map((item, index) => (
            <Card 
              key={item.id} 
              className={`faq-card mb-3 border-0 shadow-sm animate__animated animate__fadeInUp`} 
              style={{ 
                borderRadius: '16px', 
                overflow: 'hidden',
                animationDelay: `${index * 0.05}s`,
                background: 'var(--speem-background)'
              }}
            >
              <CardHeader 
                onClick={() => toggle(item.id)} 
                className={`cursor-pointer border-0 d-flex align-items-center justify-content-between p-4 ${open === item.id ? 'active-header' : ''}`}
                style={{ 
                  background: open === item.id ? 'var(--speem-gray)' : 'transparent',
                  transition: 'all 0.3s ease'
                }}
              >
                <div className="d-flex align-items-center">
                  <div 
                    className={`faq-icon-wrap mr-3 d-flex align-items-center justify-content-center`}
                    style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '12px',
                      background: open === item.id ? 'var(--speem)' : 'var(--speem-gray)',
                      color: open === item.id ? 'white' : 'var(--speem)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className={item.icon}></i>
                  </div>
                  <div>
                    <span className="text-uppercase x-small font-weight-bold opacity-6 d-block mb-1" style={{ fontSize: '10px', letterSpacing: '1px' }}>
                      {item.category}
                    </span>
                    <h5 className="mb-0 font-weight-bold" style={{ color: open === item.id ? 'var(--speem)' : 'var(--letters)' }}>
                      {item.question}
                    </h5>
                  </div>
                </div>
                <i className={`fas fa-chevron-down opacity-5 transition-all ${open === item.id ? 'rotate-180' : ''}`}></i>
              </CardHeader>
              <Collapse isOpen={open === item.id}>
                <CardBody className="p-4 pt-0 text-secondary3" style={{ fontSize: '1.05rem', lineHeight: '1.6' }}>
                  <div className="faq-answer-divider mb-3" style={{ height: '1px', background: 'var(--borders-light-clear)', opacity: 0.3 }}></div>
                  {item.answer}
                </CardBody>
              </Collapse>
            </Card>
          ))
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-search-minus display-3 text-muted mb-3 d-block"></i>
            <h3>No results found</h3>
            <p className="text-muted">Try using different keywords or explore all questions.</p>
            <button className="btn btn-link text-speem" onClick={() => setSearch('')}>Clear Search</button>
          </div>
        )}
      </div>

      <style>{`
        .cursor-pointer { cursor: pointer; }
        .rotate-180 { transform: rotate(180deg); }
        .transition-all { transition: all 0.3s ease; }
        .opacity-6 { opacity: 0.6; }
        .x-small { font-size: 0.75rem; }
        .active-header {
           border-left: 4px solid var(--speem) !important;
        }
        .faq-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.05) !important;
          transition: all 0.3s ease;
        }
      `}</style>
    </Container>
  );
};

export default FAQPage;
